import { useEffect, useState, useRef } from "react";
import {
  TextField,
  Grid,
  Select,
  MenuItem,
  IconButton,
  Autocomplete,
  Typography,
  Button,
  InputAdornment,
  Box,
  CircularProgress,
  Popper,
  ClickAwayListener,
  Tooltip,
} from "@mui/material";
import {
  PictureAsPdf as PictureAsPdfIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Description as DescriptionIcon,
  Article as ArticleIcon,
  Download as DownloadIcon,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { wmsService } from "../../utils/services/wmsProvider";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { asnService } from "../../utils/services/asnService";
import { toast } from "react-toastify";
import { isMobile } from "../../globalStyles/mobile";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import { purchaseOrderService } from "../../utils/services/purchaseOrderService";
import { supplierService } from "../../utils/services/supplierService";
import { imageService } from "../../utils/services/imageService";
import { inventoryConfigService } from "../../utils/services/inventory-config.service";

const UpdateASNForm = () => {
  const [asnData, setAsnData] = useState({});
  const [formData, setFormData] = useState({
    asn_no: "",
    bol_no: "",
    warehouse: "",
    supplier: "",
    shipment_date: null,
    expected_dispatch_date: null,
    purchase_order: null,
    sku_list: [
      {
        id: "",
        sku: "",
        quantity_requested: "",
        quantity_accepted: "",
        overpack_type: "",
        items_per_overpack: "",
        product: "",
        variant_id: "",
        instructions: "",
        no_of_overpacks: "",
        attachment: {
          key: "",
          fileName: "",
        },
        warehouse: null,
        variant: null,
        product_details: null,
      },
    ],
    driver_name: "",
    vehicle_license: "",
    client: {},
    confirmed_dispatch_date: null,
  });
  const { id } = useParams();
  const [wmsProviders, setWMSProviders] = useState([]);
  const [expectedCalendar, setExpectedCalendar] = useState(false);
  const [showShipmentCalendar, setShowShipmentCalendar] = useState(false);
  const [showConfirmedCalendar, setShowConfirmedCalendar] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const expectedCalendarRef = useRef(null);
  const confirmedCalendarRef = useRef(null);
  const shipmentCalendarRef = useRef(null);
  const roles = JSON.parse(localStorage.getItem("roles")) || [];

  useEffect(() => {
    if (id && !formData.asn_no) {
      fetchASNAndSetData();
    }
  }, [id]);

  const fetchASNAndSetData = async () => {
    try {
      setLoading(true);
      const response = await asnService.fetchById(id);
      setAsnData(response);
      fetchWMSProviders(response.client);
      setFormData({
        asn_no: response.asn_no,
        bol_no: response.bol_no,
        supplier: response.supplier,
        warehouse: response?.warehouse,
        shipment_date: response.shipment_date || null,
        expected_dispatch_date: response.expected_dispatch_date || null,
        purchase_order: response.purchase_order || null,
        sku_list: response.sku_list.map((row) => ({
          _id: row._id,
          sku: row.sku,
          quantity_requested: row.quantity_requested,
          quantity_accepted: row.quantity_accepted || row.quantity_requested,
          overpack_type: row.overpack_type,
          items_per_overpack: row.items_per_overpack,
          no_of_overpacks: row.no_of_overpacks,
          product: row.product._id || row.product,
          variant_id: row.variant_id,
          instructions: row.instructions || "",
          attachment: row.attachment || { key: "", fileName: "" },
          warehouse: row.warehouse,
          variant:
            row.product?.variants?.find((v) => v._id === row.variant_id) ||
            null,
          product_details: row.product,
        })),
        driver_name: response?.additional_info?.driver_name,
        vehicle_license: response?.additional_info?.vehicle_license,
        client: response.client,
        confirmed_dispatch_date: response.confirmed_dispatch_date || null,
      });
      fetchInventoryConfigAndSetWarehouse(response);
    } catch (error) {
      console.error("Error fetching ASN data:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeSkuRow = (id) => {
    setFormData((prev) => ({
      ...prev,
      sku_list: prev.sku_list.filter((row) => row._id !== id),
    }));
  };

  const handleSkuChange = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      sku_list: prev.sku_list.map((sku) =>
        sku._id === id ? { ...sku, [field]: value } : sku,
      ),
    }));

    // Clear field error when user starts typing
    const fieldKey = `sku_${id}_${field}`;
    if (fieldErrors[fieldKey]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  const handleInputChange = (field, value) => {
    if (field === "supplier") {
      fetchInventoryConfigAndSetWarehouse({
        sku_list: formData.sku_list,
        client: formData.client,
        supplier: value,
      });
    }

    if (field === "confirmed_dispatch_date") {
      setFormData((prev) => ({ ...prev, [field]: value, shipment_date: null }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const fetchInventoryConfigAndSetWarehouse = async (data) => {
    try {
      if (!data.sku_list.length || !data.client || !data.supplier) return;
      let resp =
        await inventoryConfigService.fetchBySkuListAndClientAndSupplier(
          data.sku_list.map((x) => x.sku),
          data.client,
          data.supplier?._id,
        );
      if (resp.total) {
        let warehouses = resp.data
          .map((x) => x.warehouse)
          .reduce((a, b) => (a[b._id] ? a : { ...a, [b._id]: b }), {});
        if (Object.keys(warehouses).length > 1) return;
        else handleInputChange("warehouse", Object.values(warehouses)[0]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Helper component for displaying inline errors
  const FieldError = ({ errorKey }) => {
    const error = fieldErrors[errorKey];
    if (!error) return null;

    return (
      <Typography
        variant="caption"
        color="error"
        sx={{
          display: "block",
          mt: 0.5,
          fontSize: "0.75rem",
          lineHeight: 1.2,
        }}
      >
        {error}
      </Typography>
    );
  };

  const handleAttachmentChange = async (e, skuId) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const file = files[0];
      try {
        setLoading(true);
        const attachmentData = await imageService.upload(file);

        setFormData((prev) => ({
          ...prev,
          sku_list: prev.sku_list.map((sku) =>
            sku._id === skuId
              ? {
                  ...sku,
                  attachment: {
                    key: attachmentData.key,
                    fileName: file.name,
                  },
                }
              : sku,
          ),
        }));
      } catch (error) {
        console.error("Attachment upload failed:", error);
        toast.error("Failed to upload attachment. Please try again.");
      } finally {
        e.target.value = null;
        setLoading(false);
      }
    }
  };
  const validateForm = () => {
    if (asnData.status === "Pending") {
      return true;
    }
    let errors = [];
    let newFieldErrors = {};

    if (asnData.type === "M2O" && !asnData.supplier) {
      if (!formData.supplier) {
        errors.push(`Please select a supplier`);
        newFieldErrors.supplier = "Please select a supplier";
      }
      if (!formData.expected_dispatch_date) {
        errors.push(`Please select an expected dispatch date`);
        newFieldErrors.expected_dispatch_date =
          "Please select an expected dispatch date";
      }
    } else {
      // if (!formData.warehouse) {
      //   errors.push(`Please select a warehouse`);
      //   newFieldErrors.warehouse = "Please select a warehouse";
      // }

      formData.sku_list.forEach((item, index) => {
        const rowNum = index + 1;
        const fieldKey = `sku_${item._id}`;

        if (!item.quantity_requested || Number(item.quantity_requested) <= 0) {
          errors.push(`${rowNum}. Requested quantity must be greater than 0.`);
          newFieldErrors[`${fieldKey}_quantity_requested`] =
            "Requested quantity must be greater than 0";
        }

        if (!item.quantity_accepted || Number(item.quantity_accepted) <= 0) {
          errors.push(`${rowNum}. Confirmed quantity must be greater than 0.`);
          newFieldErrors[`${fieldKey}_quantity_accepted`] =
            "Confirmed quantity must be greater than 0";
        }

        if (Number(item.quantity_accepted) > Number(item.quantity_requested)) {
          errors.push(
            `${rowNum}. Confirmed quantity cannot exceed requested quantity.`,
          );
          newFieldErrors[`${fieldKey}_quantity_accepted`] =
            "Cannot exceed requested quantity";
        }

        if (item.no_of_overpacks && Number(item.no_of_overpacks) <= 0) {
          errors.push(`${rowNum}. Overpacks must be greater than 0.`);
          newFieldErrors[`${fieldKey}_no_of_overpacks`] =
            "Overpacks must be greater than 0";
        }

        if (item.items_per_overpack && Number(item.items_per_overpack) <= 0) {
          errors.push(`${rowNum}. Items per overpack must be greater than 0.`);
          newFieldErrors[`${fieldKey}_items_per_overpack`] =
            "Items per overpack must be greater than 0";
        }

        if (!item.overpack_type) {
          errors.push(`${rowNum}. Overpack type is required.`);
          newFieldErrors[`${fieldKey}_overpack_type`] =
            "Overpack type is required";
        }
        if (!item.no_of_overpacks) {
          errors.push(`${rowNum}. No of overpacks is required.`);
          newFieldErrors[`${fieldKey}_no_of_overpacks`] =
            "No of overpacks is required";
        }
        if (!item.warehouse) {
          errors.push(`${rowNum}. Please select a warehouse.`);
          newFieldErrors[`${fieldKey}_warehouse`] = "Please select a warehouse";
        }
      });
    }

    setFieldErrors(newFieldErrors);

    if (errors.length > 0) {
      // toast.error(
      //   <div>
      //     <Typography
      //       variant="subtitle1"
      //       color="error"
      //       sx={{ fontWeight: "bold" }}
      //     >
      //       Validation Errors
      //     </Typography>
      //     <List dense>
      //       {errors.map((error, i) => (
      //         <ListItem key={i} sx={{ padding: 0 }}>
      //           <ListItemText primary={error} />
      //         </ListItem>
      //       ))}
      //     </List>
      //   </div>,
      //   { autoClose: 5000, position: "top-right" }
      // );
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      let obj = {
        asn_no: formData.asn_no,
        bol_no: formData.bol_no,
        client: formData.client,
        purchase_order: formData.purchase_order?._id,
        expected_dispatch_date: formData.expected_dispatch_date,
        confirmed_dispatch_date: formData.confirmed_dispatch_date,
        shipment_date: formData.shipment_date,
        warehouse: formData.warehouse?._id,
        sku_list: formData.sku_list.map((x) => ({
          _id: x._id, // Include the SKU item ID for proper update
          sku: x.sku,
          quantity_accepted: x.quantity_accepted,
          quantity_requested: x.quantity_requested,
          product: x.product,
          overpack_type: x.overpack_type,
          items_per_overpack: x.items_per_overpack,
          no_of_overpacks: x.no_of_overpacks,
          variant_id: x.variant_id,
          instructions: x.instructions || "", // Ensure instructions are included, default to empty string
          attachment: x.attachment,
          warehouse: x.warehouse?._id,
        })),
        additional_info: {
          driver_name: formData.driver_name,
          vehicle_license: formData.vehicle_license,
        },
      };
      if (!asnData.supplier) {
        obj.supplier = formData.supplier?._id;
      }
      await asnService.edit(id, obj);
      toast.success("P.O. updated successfully!");
      navigate("/purchase-order?child=true");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAndArchive = async () => {
    try {
      await asnService.edit(id, { status: "Cancelled" });
      toast.success("P.O. cancelled successfully!");
    } catch (e) {
      console.error(e);
    } finally {
      navigate("/purchase-order?child=true");
    }
  };
  const fetchWMSProviders = async (clientId) => {
    try {
      const res = await wmsService.fetch(clientId);
      setWMSProviders(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    // Click outside handling is now managed by ClickAwayListener in the Popper
  }, [
    expectedCalendar,
    showShipmentCalendar,
    expectedCalendarRef,
    confirmedCalendarRef,
    shipmentCalendarRef,
  ]);

  return (
    <>
      <Box
        sx={{
          padding: "20px",
          maxHeight: isMobile() ? "calc(100vh - 200px)" : "calc(100vh - 150px)",
          overflowY: "auto",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 9999,
              color: "white",
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        ) : (
          <></>
        )}
        {/* Basic Information Section */}
        <Typography
          variant="subtitle1"
          align="left"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          Basic Information
        </Typography>
        <Box
          sx={{
            border: "1px solid var(--border-color)",
            borderRadius: 1,
            p: 2,
            mb: 3,
            backgroundColor: "var(--bg-secondary)",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 1, textAlign: "left" }}>
                P.O. Number
              </Typography>
              <TextField
                disabled
                value={formData.asn_no}
                required
                size="small"
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 1, textAlign: "left" }}>
                Supplier
              </Typography>
              <AutocompleteWithPagination
                hideLabel
                disabled={asnData.supplier}
                selectedClient={asnData.client}
                // backgroundColor={"#FFF"}
                customLabel={"Supplier"}
                selectedItem={formData.supplier}
                onDataChange={(e) => {
                  if (e) {
                    handleInputChange("supplier", e);
                  } else {
                    handleInputChange("supplier", null);
                  }
                }}
                width={"100%"}
                optionKey={"name"}
                apiEndPoint={(limit, skip, search, client) =>
                  supplierService.fetch(
                    search,
                    client,
                    `$limit=${limit}&$skip=${skip}`,
                  )
                }
                error={!!fieldErrors.supplier}
              ></AutocompleteWithPagination>
              <FieldError errorKey="supplier" />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 1, textAlign: "left" }}>
                Master P.O.
              </Typography>
              <AutocompleteWithPagination
                disabled
                hideLabel
                selectedClient={{ _id: formData.client }}
                customLabel={"PO"}
                selectedItem={formData.purchase_order}
                onDataChange={(e) => {
                  if (e) {
                    handleInputChange("purchase_order", e);
                    // setSelectedASN(e)
                  } else {
                    handleInputChange("purchase_order", null);
                  }
                }}
                width={"100%"}
                optionKey={"no"}
                apiEndPoint={(limit, skip, search, client) =>
                  purchaseOrderService.fetch(
                    `$limit=${limit}&$skip=${skip}`,
                    client,
                    search,
                  )
                }
              ></AutocompleteWithPagination>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 1, textAlign: "left" }}>
                Bill of Lading Number
              </Typography>
              <TextField
                value={formData.bol_no}
                required
                size="small"
                fullWidth
                variant="outlined"
                onChange={(event) =>
                  handleInputChange("bol_no", event.target.value)
                }
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography
            fontFamily={"poppins"}
            fontSize={"14px"}
            fontWeight={500}
            sx={{ mb: 2, display: "flex" }}
          >
            P.O. Information
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              mt: 1,
              width: "100%",
              border: "1px solid var(--border-color)",
              padding: "20px",
              maxHeight: 650,
              overflowY: "auto",
            }}
          >
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ mb: 1, textAlign: "left" }}>
                  Expected Dispatch Date
                </Typography>
                <TextField
                  disabled
                  inputRef={expectedCalendarRef}
                  size="small"
                  value={
                    formData.expected_dispatch_date
                      ? dayjs(formData.expected_dispatch_date).format(
                          "YYYY-MM-DD",
                        )
                      : "Select Expected Delivery Date"
                  }
                  onFocus={() => setExpectedCalendar(!expectedCalendar)}
                  inputProps={{ readOnly: true }}
                  fullWidth
                  variant="outlined"
                  error={!!fieldErrors.expected_dispatch_date}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          onClick={() => setExpectedCalendar(!expectedCalendar)}
                        >
                          <CalendarIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FieldError errorKey="expected_dispatch_date" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ mb: 1, textAlign: "left" }}>
                  Confirmed Dispatch Date
                </Typography>
                <TextField
                  inputRef={confirmedCalendarRef}
                  size="small"
                  value={
                    formData.confirmed_dispatch_date
                      ? dayjs(formData.confirmed_dispatch_date).format(
                          "YYYY-MM-DD",
                        )
                      : "Select Confirmed Dispatch Date"
                  }
                  onFocus={() =>
                    setShowConfirmedCalendar(!showConfirmedCalendar)
                  }
                  inputProps={{ readOnly: true }}
                  fullWidth
                  variant="outlined"
                  error={!!fieldErrors.confirmed_dispatch_date}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          onClick={() =>
                            setShowConfirmedCalendar(!showConfirmedCalendar)
                          }
                        >
                          <CalendarIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FieldError errorKey="confirmed_dispatch_date" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ mb: 1, textAlign: "left" }}>
                  Shipment Date
                </Typography>
                <TextField
                  inputRef={shipmentCalendarRef}
                  size="small"
                  value={
                    formData.shipment_date
                      ? dayjs(formData.shipment_date).format("YYYY-MM-DD")
                      : "Select Shipment Date"
                  }
                  onFocus={() => setShowShipmentCalendar(!showShipmentCalendar)}
                  inputProps={{ readOnly: true }}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          onClick={() =>
                            setShowShipmentCalendar(!showShipmentCalendar)
                          }
                        >
                          <CalendarIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                maxHeight: "65vh",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {formData.sku_list.map((row, index) => (
                <Grid container spacing={2} key={row._id} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        position: "relative",
                        backgroundColor: "var(--bg-primary)",
                        padding: 2,
                        border: "1px solid var(--border-color)",
                        borderRadius: "4px",
                      }}
                    >
                      {formData.sku_list.length > 1 && (
                        <IconButton
                          onClick={() => removeSkuRow(row._id)}
                          sx={{ position: "absolute", top: 2, right: 5 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}

                      <Grid container spacing={2}>
                        {/* Main content area - 80% width */}
                        <Grid
                          item
                          xs={12}
                          md={asnData.type === "M2O" ? 9.6 : 12}
                        >
                          <Grid container spacing={2}>
                            {/* First row: SKU, Qty Requested, Qty Confirmed, No of Overpacks */}
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" textAlign={"left"}>
                                SKU
                              </Typography>
                              <Tooltip
                                title={`${row?.variant?.sku || row.sku} - ${
                                  row?.variant?.title
                                    ? "(" + row?.variant?.title + ")"
                                    : ""
                                } ${
                                  row?.product_details?.name
                                    ? " | " + row?.product_details?.name
                                    : ""
                                }`}
                              >
                                <TextField
                                  disabled
                                  value={`${row?.variant?.sku || row.sku} - ${
                                    row?.variant?.title
                                      ? "(" + row?.variant?.title + ")"
                                      : ""
                                  } ${
                                    row?.product_details?.name
                                      ? " | " + row?.product_details?.name
                                      : ""
                                  }`}
                                  fullWidth
                                  size="small"
                                  margin="dense"
                                  onChange={(e) =>
                                    handleSkuChange(
                                      row._id,
                                      "sku",
                                      e.target.value,
                                    )
                                  }
                                />
                              </Tooltip>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, textAlign: "left" }}
                              >
                                Warehouse
                              </Typography>
                              <Autocomplete
                                disabled
                                fullWidth
                                size="small"
                                options={wmsProviders}
                                getOptionLabel={(option) =>
                                  `${option.name} (${option.warehouse_code})` ||
                                  ""
                                }
                                value={row.warehouse}
                                onChange={(event, newValue) =>
                                  handleSkuChange(
                                    row._id,
                                    "warehouse",
                                    newValue,
                                  )
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    error={!!fieldErrors.warehouse}
                                  />
                                )}
                              />
                              <FieldError errorKey={`${row._id}_warehouse`} />
                            </Grid>

                            <Grid item xs={12} sm={3}>
                              <Typography variant="body2" textAlign={"left"}>
                                Qty Requested
                              </Typography>
                              <TextField
                                disabled
                                value={row.quantity_requested}
                                fullWidth
                                size="small"
                                margin="dense"
                                onChange={(e) =>
                                  handleSkuChange(
                                    row._id,
                                    "quantity",
                                    e.target.value,
                                  )
                                }
                              />
                            </Grid>
                            {!(asnData.type === "M2O" && !asnData.supplier) && (
                              <>
                                <Grid item xs={12} sm={3}>
                                  <Typography
                                    variant="body2"
                                    textAlign={"left"}
                                  >
                                    Qty Confirmed
                                  </Typography>
                                  <TextField
                                    value={row.quantity_accepted}
                                    size="small"
                                    type="number"
                                    fullWidth
                                    inputProps={{ min: 0 }}
                                    error={
                                      !!fieldErrors[
                                        `sku_${row._id}_quantity_accepted`
                                      ]
                                    }
                                    onChange={(e) =>
                                      handleSkuChange(
                                        row._id,
                                        "quantity_accepted",
                                        e.target.value,
                                      )
                                    }
                                    margin="dense"
                                  />
                                  <FieldError
                                    errorKey={`sku_${row._id}_quantity_accepted`}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                  <Typography
                                    variant="body2"
                                    textAlign={"left"}
                                  >
                                    No of Overpacks
                                  </Typography>
                                  <TextField
                                    value={row.no_of_overpacks}
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    error={
                                      !!fieldErrors[
                                        `sku_${row._id}_no_of_overpacks`
                                      ]
                                    }
                                    onChange={(e) =>
                                      handleSkuChange(
                                        row._id,
                                        "no_of_overpacks",
                                        e.target.value,
                                      )
                                    }
                                    margin="dense"
                                  />
                                  <FieldError
                                    errorKey={`sku_${row._id}_no_of_overpacks`}
                                  />
                                </Grid>
                              </>
                            )}

                            {/* Second row: Overpack Type, Max Items/Overpack, Instructions */}
                            {
                              <>
                                {!(
                                  asnData.type === "M2O" && !asnData.supplier
                                ) && (
                                  <Grid item xs={12} sm={3}>
                                    <Typography
                                      variant="body2"
                                      textAlign={"left"}
                                    >
                                      Overpack Type
                                    </Typography>
                                    <Select
                                      value={row.overpack_type}
                                      fullWidth
                                      size="small"
                                      error={
                                        !!fieldErrors[
                                          `sku_${row._id}_overpack_type`
                                        ]
                                      }
                                      onChange={(e) =>
                                        handleSkuChange(
                                          row._id,
                                          "overpack_type",
                                          e.target.value,
                                        )
                                      }
                                      sx={{
                                        mt: 1,
                                        "& .MuiSelect-select": {
                                          textAlign: "left",
                                          display: "flex",
                                          alignItems: "center",
                                        },
                                      }}
                                    >
                                      {[
                                        "Corrugated Carton",
                                        "Gaylord Box",
                                        "Pallet",
                                        "Crate",
                                      ].map((x) => (
                                        <MenuItem key={x} value={x}>
                                          {x}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    <FieldError
                                      errorKey={`sku_${row._id}_overpack_type`}
                                    />
                                  </Grid>
                                )}
                                {!(
                                  asnData.type === "M2O" && !asnData.supplier
                                ) && (
                                  <Grid item xs={12} sm={3}>
                                    <Typography
                                      variant="body2"
                                      textAlign={"left"}
                                    >
                                      Max Items/Overpack
                                    </Typography>
                                    <TextField
                                      value={row.items_per_overpack}
                                      fullWidth
                                      size="small"
                                      type="number"
                                      margin="dense"
                                      inputProps={{ min: 0 }}
                                      error={
                                        !!fieldErrors[
                                          `sku_${row._id}_items_per_overpack`
                                        ]
                                      }
                                      onChange={(e) =>
                                        handleSkuChange(
                                          row._id,
                                          "items_per_overpack",
                                          e.target.value,
                                        )
                                      }
                                    />
                                    <FieldError
                                      errorKey={`sku_${row._id}_items_per_overpack`}
                                    />
                                  </Grid>
                                )}
                                {asnData.type === "M2O" && (
                                  <Grid item xs={12} sm={12}>
                                    <Typography
                                      variant="body2"
                                      textAlign={"left"}
                                    >
                                      Instructions
                                    </Typography>
                                    <TextField
                                      disabled={asnData.supplier}
                                      multiline
                                      minRows={1}
                                      maxRows={3}
                                      value={row.instructions}
                                      fullWidth
                                      size="small"
                                      type="text"
                                      margin="dense"
                                      inputProps={{ min: 0 }}
                                      onChange={(e) =>
                                        handleSkuChange(
                                          row._id,
                                          "instructions",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </Grid>
                                )}
                              </>
                            }
                          </Grid>
                        </Grid>

                        {/* Attachment area - 20% width (only for M2O) */}
                        {asnData.type === "M2O" && (
                          <Grid item xs={12} md={2.4}>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 1,
                                fontSize: "0.875rem",
                                fontFamily: "Poppins",
                                textAlign: "left",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              Attach Additional Details
                            </Typography>

                            <Box
                              sx={{
                                minHeight: asnData.supplier ? 220 : 125,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 1,
                                position: "relative",
                                overflow: "hidden",
                              }}
                            >
                              {/* Show upload area if no attachment */}
                              {!row.attachment?.key && (
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: asnData.supplier ? 220 : 125,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    border: "1px dashed #bdbdbd",
                                    borderRadius: 2,
                                    bgcolor: "var(--bg-paper)",
                                    p: 2,
                                    boxSizing: "border-box",
                                  }}
                                >
                                  <UploadIcon
                                    sx={{
                                      fontSize: 40,
                                      color: "#90caf9",
                                      mb: 1,
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    size="small"
                                    disabled={asnData.supplier}
                                    sx={{
                                      fontFamily: "Poppins",
                                      fontSize: 12,
                                      minWidth: 120,
                                      mt: 1,
                                    }}
                                  >
                                    Choose File
                                    <input
                                      type="file"
                                      accept="*/*"
                                      hidden
                                      onChange={(e) =>
                                        handleAttachmentChange(e, row._id)
                                      }
                                    />
                                  </Button>
                                </Box>
                              )}

                              {/* Show attachment preview if exists */}
                              {row.attachment?.key &&
                                (() => {
                                  const config =
                                    require("../../config").default;
                                  const fileName =
                                    row.attachment.fileName?.toLowerCase() ||
                                    "";
                                  const previewUrl = `${config.baseURL}/fetch-file/${row.attachment.key}`;
                                  const isImage =
                                    /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(
                                      fileName,
                                    );

                                  return isImage ? (
                                    // Image preview
                                    <Box
                                      sx={{
                                        position: "relative",
                                        width: "100%",
                                        height: "125px",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        border: "1px dashed #bdbdbd",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        bgcolor: "var(--bg-paper)",
                                        boxSizing: "border-box",
                                        "&:hover .attachment-actions": {
                                          opacity: 1,
                                        },
                                      }}
                                    >
                                      <img
                                        src={previewUrl}
                                        alt={row.attachment.fileName}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                          borderRadius: 2,
                                          display: "block",
                                        }}
                                      />
                                      <Box
                                        className="attachment-actions"
                                        sx={{
                                          position: "absolute",
                                          top: 0,
                                          left: 0,
                                          width: "100%",
                                          height: "100%",
                                          bgcolor: "rgba(0,0,0,0.35)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: 2,
                                          opacity: 0,
                                          transition: "opacity 0.2s",
                                          borderRadius: 2,
                                          zIndex: 2,
                                        }}
                                      >
                                        {!asnData.supplier && (
                                          <IconButton
                                            color="error"
                                            size="small"
                                            sx={{
                                              bgcolor: "var(--bg-paper)",
                                              "&:hover": { bgcolor: "#ffeaea" },
                                            }}
                                            onClick={() => {
                                              handleSkuChange(
                                                row._id,
                                                "attachment",
                                                { key: "", fileName: "" },
                                              );
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        )}
                                        <IconButton
                                          color="primary"
                                          size="small"
                                          sx={{
                                            bgcolor: "var(--bg-paper)",
                                            "&:hover": { bgcolor: "#e3f2fd" },
                                          }}
                                          component="a"
                                          href={previewUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          download={row.attachment.fileName}
                                        >
                                          <DownloadIcon />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  ) : (
                                    // File preview
                                    <Box
                                      sx={{
                                        position: "relative",
                                        width: "100%",
                                        height: "125px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: "1px dashed #bdbdbd",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        bgcolor: "var(--bg-secondary)",
                                        p: 2,
                                        boxSizing: "border-box",
                                        "&:hover .attachment-actions": {
                                          opacity: 1,
                                        },
                                        cursor: "pointer",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          flex: 1,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          width: "100%",
                                        }}
                                      >
                                        {(() => {
                                          if (fileName.endsWith(".pdf"))
                                            return (
                                              <PictureAsPdfIcon
                                                color="error"
                                                sx={{ fontSize: 64 }}
                                              />
                                            );
                                          if (
                                            fileName.endsWith(".doc") ||
                                            fileName.endsWith(".docx")
                                          )
                                            return (
                                              <DescriptionIcon
                                                sx={{
                                                  color: "#1976d2",
                                                  fontSize: 64,
                                                }}
                                              />
                                            );
                                          if (
                                            fileName.endsWith(".xls") ||
                                            fileName.endsWith(".xlsx")
                                          )
                                            return (
                                              <Box
                                                sx={{
                                                  position: "relative",
                                                  display: "inline-flex",
                                                }}
                                              >
                                                <InsertDriveFileIcon
                                                  sx={{
                                                    color: "#388e3c",
                                                    fontSize: 64,
                                                  }}
                                                />
                                                <Typography
                                                  sx={{
                                                    position: "absolute",
                                                    right: 15,
                                                    bottom: 6,
                                                    fontSize: 13,
                                                    fontWeight: 700,
                                                    color: "white",
                                                    bgcolor: "#388e3c",
                                                    px: "3px",
                                                    borderRadius: "2px",
                                                    letterSpacing: 0.5,
                                                    pointerEvents: "none",
                                                  }}
                                                >
                                                  XLS
                                                </Typography>
                                              </Box>
                                            );
                                          if (fileName.endsWith(".txt"))
                                            return (
                                              <ArticleIcon
                                                sx={{
                                                  color: "#616161",
                                                  fontSize: 64,
                                                }}
                                              />
                                            );
                                          return (
                                            <InsertDriveFileIcon
                                              sx={{
                                                color: "#757575",
                                                fontSize: 64,
                                              }}
                                            />
                                          );
                                        })()}
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontFamily: "Poppins",
                                          fontSize: 12,
                                          color: "var(--text-tertiary)",
                                          maxWidth: "100%",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          mb: 1,
                                        }}
                                      >
                                        {row.attachment.fileName}
                                      </Typography>
                                      <Box
                                        className="attachment-actions"
                                        sx={{
                                          position: "absolute",
                                          top: 0,
                                          left: 0,
                                          width: "100%",
                                          height: "100%",
                                          bgcolor: "rgba(0,0,0,0.35)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: 2,
                                          opacity: 0,
                                          transition: "opacity 0.2s",
                                          borderRadius: 2,
                                          zIndex: 2,
                                        }}
                                      >
                                        {!asnData.supplier && (
                                          <IconButton
                                            color="error"
                                            size="small"
                                            sx={{
                                              bgcolor: "var(--bg-paper)",
                                              "&:hover": { bgcolor: "#ffeaea" },
                                            }}
                                            onClick={() => {
                                              handleSkuChange(
                                                row._id,
                                                "attachment",
                                                { key: "", fileName: "" },
                                              );
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        )}
                                        <IconButton
                                          color="primary"
                                          size="small"
                                          sx={{
                                            bgcolor: "var(--bg-paper)",
                                            "&:hover": { bgcolor: "#e3f2fd" },
                                          }}
                                          component="a"
                                          href={previewUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          download={row.attachment.fileName}
                                        >
                                          <DownloadIcon />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  );
                                })()}
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
        </Box>

        {!(asnData.type === "M2O" && !asnData.supplier) &&
        asnData.status !== "Pending" &&
        roles.some((a) => a.name === "Admin") ? (
          <Box>
            {/* Additional Information Section */}
            <Typography
              variant="subtitle1"
              align="left"
              sx={{ mb: 2, fontWeight: "bold" }}
            >
              Additional Information
            </Typography>
            <Box
              sx={{
                border: "1px solid var(--border-color)",
                borderRadius: 1,
                p: 2,
                mb: 3,
                backgroundColor: "var(--bg-secondary)",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: "left" }}>
                    Driver Name
                  </Typography>
                  <TextField
                    value={formData.driver_name}
                    required
                    fullWidth
                    size="small"
                    onChange={(e) =>
                      handleInputChange("driver_name", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: "left" }}>
                    Vehicle License Plate
                  </Typography>
                  <TextField
                    value={formData.vehicle_license}
                    required
                    fullWidth
                    size="small"
                    onChange={(e) =>
                      handleInputChange("vehicle_license", e.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        ) : (
          <></>
        )}
      </Box>
      {/* {(expectedCalendar || showShipmentCalendar || showConfirmedCalendar) && ( */}
      <Popper
        open={expectedCalendar || showShipmentCalendar || showConfirmedCalendar}
        anchorEl={
          expectedCalendar
            ? expectedCalendarRef.current
            : showConfirmedCalendar
              ? confirmedCalendarRef.current
              : shipmentCalendarRef.current
        }
        placement="bottom-start"
        disablePortal={false}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
            },
          },
        ]}
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            // Don't close if clicking on any of the calendar input fields
            if (
              expectedCalendarRef.current?.contains(event.target) ||
              confirmedCalendarRef.current?.contains(event.target) ||
              shipmentCalendarRef.current?.contains(event.target)
            ) {
              return;
            }
            setExpectedCalendar(false);
            setShowShipmentCalendar(false);
            setShowConfirmedCalendar(false);
          }}
        >
          <Box
            sx={{
              bgcolor: "var(--bg-paper)",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
              borderRadius: 1,
              p: 1,
              width: 300,
              border: "1px solid var(--border-color)",
            }}
          >
            <Calendar
              value={(() => {
                const dateValue = showShipmentCalendar
                  ? formData.shipment_date
                  : showConfirmedCalendar
                    ? formData.confirmed_dispatch_date
                    : formData.expected_dispatch_date;
                return dateValue ? new Date(dateValue) : new Date();
              })()}
              onClickDay={(date) => {
                console.log("Calendar onClickDay:", date, {
                  showShipmentCalendar,
                  showConfirmedCalendar,
                  expectedCalendar,
                });

                if (showShipmentCalendar) {
                  handleInputChange("shipment_date", date);
                  setShowShipmentCalendar(false);
                } else if (showConfirmedCalendar) {
                  handleInputChange("confirmed_dispatch_date", date);
                  setShowConfirmedCalendar(false);
                } else {
                  handleInputChange("expected_dispatch_date", date);
                  setExpectedCalendar(false);
                }
              }}
              minDate={
                showShipmentCalendar && formData.confirmed_dispatch_date
                  ? new Date(formData.confirmed_dispatch_date)
                  : new Date()
              }
              tileClassName="custom-calendar-tile"
              className="custom-calendar"
            />
          </Box>
        </ClickAwayListener>
      </Popper>
      {/* )} */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "fixed",
          bottom: 10,
          left: -10,
          right: 0,
          padding: "5px",
          backgroundColor: "var(--bg-primary)",
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Button
          onClick={() => navigate("/purchase-order?child=true")}
          variant="outlined"
        >
          Cancel
        </Button>
        {asnData.status === "Pending" ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitAndArchive}
          >
            Cancel and Archive
          </Button>
        ) : null}

        <Button variant="contained" color="error" onClick={handleSubmit}>
          {asnData.status === "Pending" ? "Approve" : "Update"}
        </Button>
      </Box>
    </>
  );
};

export default UpdateASNForm;
